// FILE: src/components/ModeSelector.js
// Step 2 of the onboarding flow — user picks the session type
// (Full Exam, Practice Exam, or Targeted Test) for the certification they
// already chose.  Reads config to show accurate question counts and durations
// per mode.

import React from 'react';
import SessionStartStatus from '../shared/SessionStartStatus';
import { getExamConfig } from '../../config/exams';
import { isFeatureEnabled } from '../../config/featureFlags';
import mcss from './ModeSelector.module.css';
import ScreenHeader from '../shared/ScreenHeader';
import { listContentLinks } from '../../config/contentLinks';

const modes = [
  {
    id: 'full',
    label: 'Full Exam',
    icon: '📋',
    description: (config) =>
      `${config.fullExam.totalQuestions} questions · ${config.fullExam.durationMinutes} min`,
    detail: 'Complete question set in exam order.',
  },
  {
    id: 'practice',
    label: 'Practice Exam',
    icon: '🎯',
    description: (config) =>
      `${config.practiceExam.defaultQuestions} questions · ${config.practiceExam.durationMinutes} min`,
    detail: 'Randomly selected questions for quick review.',
  },
  {
    id: 'caseStudy',
    label: 'Case Study Practice',
    icon: '🗂️',
    description: (config) =>
      config.caseStudies?.summary || 'One scenario · a connected series of questions',
    detail: 'Analyze a realistic project scenario with artifacts and evolving events.',
    // Rendered only when the cert opts in AND the global flag is on.
    gated: (config) => !(config.caseStudies && config.caseStudies.enabled && isFeatureEnabled('caseStudiesEnabled')),
  },
  {
    id: 'targetedTest',
    label: 'Targeted Test',
    icon: '🎛️',
    description: (config) => {
      const counts = config.targetedTest?.allowedCounts || [10, 15, 20, 25];
      return `${counts[0]}–${counts[counts.length - 1]} questions · pick your focus`;
    },
    detail: 'Focus on specific exam areas you want to improve.',
  },
];

const styles = {
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '0',
    gap: '20px',
  },
  buttonRow: {
    display: 'flex',
    gap: '24px',
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  // Tiles now live INSIDE the navy banner, so the old hover treatment
  // (flip to navy) would vanish against the background. Hover = gold ring +
  // a small lift instead; the card stays light for contrast.
  guidesRow: {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignItems: 'center',
    alignSelf: 'stretch',
  },
  guidesLabel: {
    fontSize: '12px',
    letterSpacing: '1px',
    textTransform: 'uppercase',
    color: 'var(--muted)',
    fontWeight: 700,
  },
  guideChip: (isHovered) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 16px',
    borderRadius: '999px',
    background: 'var(--panel)',
    border: `2px solid ${isHovered ? 'var(--gold)' : 'var(--line-strong)'}`,
    color: 'var(--ink)',
    fontSize: '13px',
    fontWeight: 600,
    textDecoration: 'none',
    boxShadow: isHovered ? '0 4px 12px rgba(16,24,64,.12)' : 'none',
    transform: isHovered ? 'translateY(-1px)' : 'none',
    transition: 'border-color 0.15s, box-shadow 0.15s, transform 0.15s',
  }),
  modeButton: (isHovered) => ({
    padding: '24px 30px',
    minWidth: '210px',
    flex: '1 1 210px',
    maxWidth: '300px',
    cursor: 'pointer',
    border: `2px solid ${isHovered ? 'var(--gold)' : 'transparent'}`,
    borderRadius: 'var(--radius-sm)',
    background: 'var(--panel)',
    color: 'var(--ink)',
    textAlign: 'center',
    transform: isHovered ? 'translateY(-3px)' : 'none',
    boxShadow: isHovered ? '0 8px 20px rgba(0,0,0,0.35)' : '0 2px 8px rgba(0,0,0,0.25)',
    transition: 'transform 0.15s, border-color 0.15s, box-shadow 0.15s',
  }),
  modeIcon: {
    display: 'block',
    fontSize: '2rem',
    marginBottom: '8px',
  },
  modeLabel: {
    display: 'block',
    fontSize: '1.2rem',
    fontWeight: 'bold',
    marginBottom: '8px',
  },
  modeDesc: () => ({
    display: 'block',
    fontSize: '0.85rem',
    fontWeight: 'normal',
    color: 'var(--muted)',
    marginBottom: '4px',
  }),
  modeDetail: () => ({
    display: 'block',
    fontSize: '0.8rem',
    fontWeight: 'normal',
    color: 'var(--muted)',
    fontStyle: 'italic',
  }),
};

/**
 * @param {string}   examId       - "pmp" | "cpmai" | "pmi-acp"
 * @param {function} onSelectMode - called with mode string
 *                                  ("full" | "practice" | "targetedTest")
 * @param {function} onBack       - returns user to ExamSelector
 */
export default function ModeSelector({ examId, onSelectMode, onBack, sessionStart, onRetrySessionStart }) {
  const [hovered, setHovered] = React.useState(null);
  const config = getExamConfig(examId);
  const guides = listContentLinks(examId);

  if (!config) return null;

  // PR 2A: a session start for THIS cert is loading its bank — disable the
  // initiating mode buttons (but not Back) and show a concise inline status.
  const starting = !!sessionStart
    && sessionStart.status === 'loading'
    && (sessionStart.certId == null || sessionStart.certId === examId);

  return (
    <div style={styles.wrapper} className={mcss.wrapper}>
      {/* Standard topmast (ScreenHeader banner) — PM Pixel rides the banner. */}
      <div style={{ alignSelf: 'stretch' }}>
        <ScreenHeader
          variant="banner"
          coachPose="notepad"
          coachHeightPx={212}
          coachCentered
          onBack={onBack}
          backLabel="Back to Certification Select"
          backTestId="mode-selector-back"
          eyebrow="Session Setup"
          title={config.displayName}
          subtitle="Choose your session type"
        >
          {/* Mode tiles live inside the hero banner (better use of the
              navy real estate; cards pop against the dark background). */}
          <div style={styles.buttonRow} className={mcss.buttonRow}>
        {modes.filter((m) => !m.gated || !m.gated(config)).map((mode) => (
          <button
            key={mode.id}
            data-testid={`mode-button-${mode.id}`}
            style={styles.modeButton(hovered === mode.id)}
            disabled={starting}
            aria-disabled={starting}
            onMouseEnter={() => setHovered(mode.id)}
            onMouseLeave={() => setHovered(null)}
            onClick={() => { if (!starting) onSelectMode(mode.id); }}
          >
            <span style={styles.modeIcon}>{mode.icon}</span>
            <span style={styles.modeLabel}>{mode.label}</span>
            <span style={styles.modeDesc(hovered === mode.id)}>
              {mode.description(config)}
            </span>
            <span style={styles.modeDetail(hovered === mode.id)}>
              {mode.detail}
            </span>
          </button>
        ))}
          </div>
        </ScreenHeader>
      </div>

      {/* Common questions — free article links, their own row under the tiles. */}
      {guides.length > 0 && (
        <div style={styles.guidesRow} data-testid="guide-links-row">
          <span style={styles.guidesLabel}>Common {config.displayName} Questions</span>
          {guides.map((g) => (
            <a
              key={g.id}
              href={g.href}
              target="_blank"
              rel="noopener noreferrer"
              title="Opens in a new tab"
              style={styles.guideChip(hovered === `guide-${g.id}`)}
              onMouseEnter={() => setHovered(`guide-${g.id}`)}
              onMouseLeave={() => setHovered(null)}
              data-testid={`guide-chip-${g.id}`}
            >
              <span aria-hidden="true">{g.icon}</span>
              {g.label}
              <span aria-hidden="true" style={{ fontSize: '11px', opacity: 0.55 }}>↗</span>
            </a>
          ))}
        </div>
      )}

      {sessionStart && (
        <SessionStartStatus
          status={sessionStart.status}
          error={sessionStart.error}
          canRetry={sessionStart.canRetry}
          onRetry={onRetrySessionStart}
        />
      )}

    </div>
  );
}
